import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useZminky, ZminkaText } from "../../components/ZminkaInput.jsx";
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { Calendar, User, ChevronDown, ChevronLeft, Pin, PinOff, Rocket, Eye, CheckCircle2, Archive, ArchiveRestore, RefreshCw, Trash2, Plus, ListChecks, Flame, Equal, ChevronsDown, X, Send, Repeat, MapPin, ExternalLink, AlertCircle, Pencil, Lock, BookOpen, Link2 } from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import { useZamekRolovani, useUzkaObrazovka } from "../../lib/obrazovka.js";
import { getCurrentUser } from "../../lib/auth.js";
import KlientPicker from "../../components/KlientPicker.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import UkolHistorie from "../../components/UkolHistorie.jsx";
import { formatDateTime } from "../../lib/format.js";
import { toast } from "../../components/Toast.jsx";
import { PlovouciNabidka } from "../../components/ui.jsx";

// === TRENÉR ÚKOLY - Kanban redesign (29.5.2026) ===
// 4 sloupce: Naplánované → V procesu → Ke kontrole → Hotovo (archiv skryt).
// Karta: barevný levý proužek dle priority, pill priorita + rutina + deadline, klient chip, ikony.
// Klik na kartu → TaskDetailModal s popisem, podúkoly, komentáři, klient pickerem.
// Quick add: + ikona v hlavičce sloupce → inline input.
// Status workflow: Trenér posune do Ke kontrole; manager/head_coach schválí → Hotovo,
// nebo vrátí zpět do V procesu s důvodem (= komentář).

// 29.5.2026 v2: 3 hlavní sloupce (aktivní práce). Hotovo + Archiv vyňaty do Pinned groups
// (pill boxy nad boardem, klik = modal s listem).
const COLUMNS = [
  { key: "naplanovane", label: "Naplánované", icon: Pin, dot: "#854F0B", bg: "bg-amber-50/40", header: "text-amber-800" },
  { key: "v_procesu",   label: "V procesu",   icon: Rocket, dot: "#534AB7", bg: "bg-brand-50/40", header: "text-brand-800" },
  { key: "ke_kontrole", label: "Ke kontrole", icon: Eye, dot: "#D4537E", bg: "bg-pink-50/40", header: "text-pink-800" },
];

// Pinned groups - Hotovo + Archiv. Rozklikávací pill box.
const PINNED_GROUPS = [
  { key: "hotovo", label: "Hotovo", emoji: "✅", dot: "#0F6E56", bg: "bg-emerald-50",   border: "border-emerald-200", text: "text-emerald-800" },
  { key: "archiv", label: "Archiv", emoji: "🗂️", dot: "#5F5E5A", bg: "bg-slate-100",     border: "border-slate-200",   text: "text-slate-700" },
];

const PRIORITY_META = {
  High:   { label: "Vysoká", border: "#A32D2D", bg: "bg-red-50",   text: "text-red-800",   icon: Flame },
  Medium: { label: "Střední", border: "#BA7517", bg: "bg-amber-50", text: "text-amber-800", icon: Equal },
  Low:    { label: "Nízká",  border: "#5F5E5A", bg: "bg-slate-100",text: "text-slate-700", icon: ChevronsDown },
};

function todayIso() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function deadlineLabel(iso, today) {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { text: iso, tone: "neutral" };
  if (iso < today) {
    const days = Math.round((new Date(today + "T00:00:00") - new Date(iso + "T00:00:00")) / 86400000);
    return { text: `${days} d po`, tone: "overdue" };
  }
  if (iso === today) return { text: "Dnes", tone: "today" };
  const tmrw = new Date(today + "T00:00:00");
  tmrw.setDate(tmrw.getDate() + 1);
  if (iso === tmrw.toISOString().substring(0, 10)) return { text: "Zítra", tone: "tomorrow" };
  return { text: `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.`, tone: "future" };
}

const DEADLINE_TONE = {
  overdue:  "bg-red-50 text-red-800 border-red-200",
  today:    "bg-amber-50 text-amber-800 border-amber-200",
  tomorrow: "bg-blue-50 text-blue-800 border-blue-200",
  future:   "bg-slate-50 text-slate-700 border-slate-200",
  neutral:  "bg-slate-50 text-slate-700 border-slate-200",
};

export default function TrenerUkoly() {
  const { activeTrener } = useOutletContext();
  const user = getCurrentUser();
  const userRole = String(user?.role || "").toLowerCase();
  // 3.9.2026 Head Coach Junior: trenérka vede úkoly trenérů na své lokalitě.
  // Není to role, ale příznak, takže zůstává trenérkou a k tomu má manažerská
  // práva k úkolům kolegů. Sebe v tom přehledu nemá, backend ji vynechá.
  const vedeLokalitu = String(user?.vedeLokalitu || "").trim();
  const [vedenyTrener, setVedenyTrener] = useState("");
  const canApprove = ["owner", "manager", "head_coach"].includes(userRole) || (!!vedeLokalitu && !!vedenyTrener);
  // 13.8.2026 (zadani Ladi): manager úkoly trenérů NEmaže, jen archivuje
  // (dohledatelnost). Mazat smí owner; trenér svůj vlastní v detailu.
  const canDelete = userRole === "owner";

  // includeArchive=1 - backend by default skrývá archiv. Pinned groups (Archiv pill) potřebuje seznam.
  // Když vedoucí lokality kouká na kolegu, tahá se jeho jméno místo vlastního.
  const zobrazenyTrener = vedenyTrener || activeTrener;
  const pilotParam = zobrazenyTrener && zobrazenyTrener !== "Vyber trenéra"
    ? `?pilot=${encodeURIComponent(zobrazenyTrener)}&includeArchive=1`
    : "?includeArchive=1";
  const { data: tasksData, setData: setTasksData, loading, error, refetch, refreshing } = useApiData(`/api/trener-ukoly${pilotParam}`);

  const tasksByStatus = tasksData?.byStatus || { naplanovane: [], v_procesu: [], ke_kontrole: [], hotovo: [], archiv: [] };
  // Řazení v rámci sloupce: priorita (Vysoká → Nízká), pak deadline (nejbližší první, prázdné nakonec).
  const PRIO_ORDER = { high: 0, medium: 1, low: 2 };
  const sortTasks = (arr) => [...(arr || [])].sort((a, b) => {
    const pa = PRIO_ORDER[String(a.priority || "medium").toLowerCase()] ?? 1;
    const pb = PRIO_ORDER[String(b.priority || "medium").toLowerCase()] ?? 1;
    if (pa !== pb) return pa - pb;
    return String(a.deadline || "9999-12-31").localeCompare(String(b.deadline || "9999-12-31"));
  });
  const [openTaskId, setOpenTaskId] = useState(null);
  const [statusChanging, setStatusChanging] = useState(null);
  const [openGroup, setOpenGroup] = useState(null); // "hotovo" | "archiv"
  // Na telefonu se kanban nevejde, ukazuje se jeden sloupec a přepínač nad ním.
  const uzka = useUzkaObrazovka();
  const [vybranySloupec, setVybranySloupec] = useState("v_procesu");
  const [sloupecOtevren, setSloupecOtevren] = useState(false);
  const sloupecRef = useRef(null);
  // 29.5.2026: styled confirm dialog pro destruktivní akce (místo nativního window.confirm).
  const [pendingDelete, setPendingDelete] = useState(null); // { taskId, title }
  const [pendingArchive, setPendingArchive] = useState(null); // { taskId, title } manager potvrzuje archivaci
  const [archivBezKomentare, setArchivBezKomentare] = useState(null); // { taskId, title } upozorneni: chybi komentar

  const today = todayIso();

  // Optimistic status change (drag&drop a klik šipek). Karta skočí do cílového sloupce
  // okamžitě, API běží na pozadí. Na chybě rollback + refetch sync.
  // 29.5.2026: backend může vrátit redirected:true pokud trenér zkouší skok do Hotovo
  // u needs_review=true úkolu → server přesměruje na ke_kontrole.
  const changeStatus = useCallback(async (taskId, newStatus) => {
    let prevSnapshot = null;
    setTasksData((prev) => {
      if (!prev) return prev;
      prevSnapshot = prev;
      const newBy = {};
      for (const k of Object.keys(prev.byStatus || {})) newBy[k] = [...(prev.byStatus[k] || [])];
      // Odeber kartu z původního sloupce
      let moved = null;
      for (const k of Object.keys(newBy)) {
        const i = newBy[k].findIndex((t) => String(t.id) === String(taskId));
        if (i >= 0) { moved = { ...newBy[k][i], status: newStatus }; newBy[k].splice(i, 1); break; }
      }
      if (!moved) return prev;
      if (newStatus === "hotovo") moved.hotovo_kdy = todayIso();
      if (!newBy[newStatus]) newBy[newStatus] = [];
      newBy[newStatus].push(moved);
      const newTasks = (prev.tasks || []).map((t) => String(t.id) === String(taskId) ? { ...t, status: newStatus, hotovo_kdy: moved.hotovo_kdy } : t);
      return { ...prev, byStatus: newBy, tasks: newTasks };
    });
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ id: taskId, status: newStatus }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Update selhal");
      // Backend mohl přesměrovat (needs_review redirect) - silent refetch synchronizuje.
      if (j.redirected) {
        refetch();
        if (j.reason === "needs_review") {
          // Nehazardně přesunuto do Ke kontrole - trenér to musí vědět.
          setTimeout(() => toast("Tento úkol vyžaduje kontrolu manažerem. Přesunuto do 'Ke kontrole'.", "error"), 100);
        }
      }
    } catch (e) {
      // Rollback
      if (prevSnapshot) setTasksData(prevSnapshot);
      toast(`Chyba: ${e.message}`, "error");
    }
  }, [refetch, setTasksData]);

  const requestReview = useCallback(async (taskId) => {
    setStatusChanging(taskId);
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "requestReview", id: taskId }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Posun selhal");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setStatusChanging(null); }
  }, [refetch]);

  const approveReview = useCallback(async (taskId) => {
    setStatusChanging(taskId);
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "approveReview", id: taskId }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Schválení selhalo");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setStatusChanging(null); }
  }, [refetch]);

  const rejectReview = useCallback(async (taskId) => {
    const reason = prompt("Důvod vrácení k přepracování (vidí trenér):");
    if (!reason || !reason.trim()) return;
    setStatusChanging(taskId);
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "rejectReview", id: taskId, reason: reason.trim() }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Zamítnutí selhalo");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setStatusChanging(null); }
  }, [refetch]);

  // requestDelete jen otevře confirm dialog. Skutečné smazání běží v doDelete (po potvrzení).
  const requestDelete = useCallback((taskId, title) => {
    setPendingDelete({ taskId: String(taskId), title: title || "" });
  }, []);
  const doDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const taskId = pendingDelete.taskId;
    setPendingDelete(null);
    setStatusChanging(taskId);
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "delete", id: String(taskId) }) });
      if (!r.ok) throw new Error("Smazání selhalo");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setStatusChanging(null); }
  }, [pendingDelete, refetch]);
  const deleteTask = requestDelete;

  // Archivace managerem (13.8.2026): před archivací MUSÍ mít úkol komentář
  // s důvodem. Bez komentáře se archivace nepustí (upozornění), s komentářem
  // se ještě potvrzuje. Owner archivuje rovnou bez podmínky.
  const requestArchive = useCallback(async (taskId, title) => {
    if (userRole !== "manager") { changeStatus(taskId, "archiv"); return; }
    try {
      const r = await apiFetch(`/api/trener-ukoly?taskId=${encodeURIComponent(taskId)}`);
      const j = await r.json();
      // Pocita se jen VLASTNI komentar archivujiciho managera (13.8.2026).
      const mujEmail = String(user?.email || "").toLowerCase();
      const mojeZive = (j.komentare || []).filter((c) => !c.archived && String(c.autor_email || "").toLowerCase() === mujEmail);
      if (mojeZive.length === 0) { setArchivBezKomentare({ taskId: String(taskId), title: title || "" }); return; }
      setPendingArchive({ taskId: String(taskId), title: title || "" });
    } catch (e) {
      toast(`Nepodařilo se ověřit komentáře úkolu: ${e.message}`, "error");
    }
  }, [userRole, changeStatus]);

  // Optimistic create - karta se objeví okamžitě, API běží na pozadí.
  // Po success refetch v pozadí synchronizuje reálné ID + vytvoreno.
  const createTask = useCallback(async (payload) => {
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      vytvoreno: todayIso(),
      zdroj: payload.zdroj || "owner",
      pilot: payload.pilot,
      nazev: payload.nazev,
      popis: payload.popis || "",
      deadline: payload.deadline || "",
      priority: payload.priority || "Medium",
      tags: payload.tags || "",
      status: payload.status || "naplanovane",
      hotovo_kdy: "",
      klient_id: payload.klient_id || "",
      klient_name: payload.klient_name || "",
      lokalita: payload.lokalita || "",
    };
    // Lokální merge do byStatus i tasks
    setTasksData((prev) => {
      if (!prev) return prev;
      const nextByStatus = { ...prev.byStatus };
      const col = optimistic.status;
      nextByStatus[col] = [...(nextByStatus[col] || []), optimistic];
      return { ...prev, tasks: [...(prev.tasks || []), optimistic], byStatus: nextByStatus };
    });
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "create", task: payload }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Vytvoření selhalo");
      // Background refetch - synchronizuje skutečné ID + případné server-side defaults.
      refetch();
      return j;
    } catch (e) {
      // Rollback při chybě
      setTasksData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: (prev.tasks || []).filter((t) => t.id !== tempId),
          byStatus: Object.fromEntries(Object.entries(prev.byStatus || {}).map(([k, v]) => [k, v.filter((t) => t.id !== tempId)])),
        };
      });
      throw e;
    }
  }, [refetch, setTasksData]);

  // 29.5.2026 fix: počty se počítají JEN z viditelných sloupců (byStatus). Předchozí
  // logika počítala přes tasksData.tasks které mohlo obsahovat ghost rows s neznámým
  // statusem → header lhal ("1 aktivních") i když sloupce byly prázdné.
  const visibleTasks = [
    ...(tasksByStatus.naplanovane || []),
    ...(tasksByStatus.v_procesu || []),
    ...(tasksByStatus.ke_kontrole || []),
  ];
  const totalActive = visibleTasks.length;
  const overdueCount = visibleTasks.filter((t) => t.deadline && t.deadline < today).length;
  const todayCount = visibleTasks.filter((t) => t.deadline === today).length;

  // 29.5.2026 v8: ?new=1 query → automaticky vytvořit draft úkol + otevřít detail.
  // Použito z Dashboard "Nový úkol" CTA pro UX bez Quick Add inline workflow.
  // useRef hlídá aby se efekt spustil jen jednou (StrictMode by jinak 2× volal).
  const [searchParams, setSearchParams] = useSearchParams();
  const newDraftFiredRef = useRef(false);
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    if (newDraftFiredRef.current) return;
    if (!activeTrener || activeTrener === "Vyber trenéra") return;
    newDraftFiredRef.current = true;
    (async () => {
      try {
        const r = await createTask({ nazev: "Nový úkol", pilot: activeTrener, status: "naplanovane", priority: "Medium" });
        if (r?.id) setOpenTaskId(String(r.id));
      } catch (e) {
        console.warn("Auto-create draft selhalo:", e.message);
      } finally {
        // odstranit query param aby refresh nepřeotevřel nový draft
        searchParams.delete("new");
        setSearchParams(searchParams, { replace: true });
      }
    })();
  }, [searchParams, activeTrener, createTask, setSearchParams]);

  // ?open=<id> → rovnou otevrit detail konkretniho ukolu (deep-link z Dashboard widgetu).
  useEffect(() => {
    // FW-146: starší odkazy ze Slacku (zmínky) mají ?task=<id>, berou se stejně.
    const openId = searchParams.get("open") || searchParams.get("task");
    if (!openId) return;
    setOpenTaskId(String(openId));
    searchParams.delete("open");
    searchParams.delete("task");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const vedeniTreneri = tasksData?.vedeni?.treneri || [];

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Přepínač pro vedoucí lokality (Head Coach Junior). Vlastní úkoly jsou
          první volba, dál trenéři té lokality. Sebe v seznamu nemá. */}
      {vedeLokalitu && vedeniTreneri.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 text-sm shadow-sm flex-wrap">
          <span className="text-slate-600 font-medium">Úkoly lokality {tasksData?.vedeni?.lokalita || vedeLokalitu}:</span>
          <button type="button" onClick={() => setVedenyTrener("")}
            className={`min-h-[34px] px-3 rounded-full border text-xs font-bold transition-colors ${!vedenyTrener ? "bg-brand-600 text-white border-brand-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            Moje úkoly
          </button>
          {vedeniTreneri.map((jm) => (
            <button key={jm} type="button" onClick={() => setVedenyTrener(jm)}
              className={`min-h-[34px] px-3 rounded-full border text-xs font-bold transition-colors demo-sensitive ${vedenyTrener === jm ? "bg-brand-600 text-white border-brand-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
              {jm}
            </button>
          ))}
        </div>
      )}
      <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-11 sm:w-11">
            <ListChecks className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-xl font-bold leading-tight sm:text-2xl">Úkoly</div>
            <p className="mt-1 text-xs leading-snug text-white/75 sm:text-sm">
              {totalActive} aktivních{overdueCount > 0 ? `, ${overdueCount} po termínu` : ""}{todayCount > 0 ? `, ${todayCount} dnes` : ""}
            </p>
          </div>
          <button
            onClick={refetch}
            disabled={refreshing}
            aria-label="Obnovit"
            title="Obnovit"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 disabled:opacity-40 sm:h-10 sm:w-10 sm:hover:bg-white/20"
          >
            <RefreshCw className={`h-[18px] w-[18px] ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">Chyba: {error}</div>
      )}

      <div className="glass min-w-0 p-4 sm:p-5">
        {uzka && (
          <div ref={sloupecRef} className="relative mb-2.5">
            <button
              type="button"
              onClick={() => setSloupecOtevren((o) => !o)}
              aria-expanded={sloupecOtevren}
              className="flex min-h-[60px] w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 text-left shadow-sm"
            >
              <span className="shrink-0 rounded-full bg-brand-light px-3 py-1.5 text-sm font-semibold text-brand-deep">Sloupec</span>
              <span className="min-w-0 flex-1 text-base font-bold text-slate-900">
                {COLUMNS.find((c) => c.key === vybranySloupec)?.label || "V procesu"}
              </span>
              <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${sloupecOtevren ? "rotate-180" : ""}`} />
            </button>
            <PlovouciNabidka kotva={sloupecRef} otevreno={sloupecOtevren} onZavrit={() => setSloupecOtevren(false)} role="listbox" className="p-1.5 shadow-glass" maxVyska={420}>
                {COLUMNS.map((c) => {
                  const vybrano = vybranySloupec === c.key;
                  return (
                    <button
                      key={c.key}
                      role="option"
                      aria-selected={vybrano}
                      onClick={() => { setVybranySloupec(c.key); setSloupecOtevren(false); }}
                      className={`flex min-h-[48px] w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm ${vybrano ? "bg-brand-light font-semibold text-brand-deep" : "text-slate-700"}`}
                    >
                      {vybrano ? <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" /> : <span className="h-4 w-4 shrink-0" />}
                      <span className="min-w-0 flex-1">{c.label}</span>
                      <span className={`shrink-0 rounded-lg px-1.5 py-0.5 text-xs font-bold tabular-nums ${vybrano ? "bg-white text-brand-deep" : "bg-slate-100 text-slate-600"}`}>
                        {(tasksByStatus[c.key] || []).length}
                      </span>
                    </button>
                  );
                })}
              </PlovouciNabidka>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {PINNED_GROUPS.map((g) => {
            const count = (tasksByStatus[g.key] || []).length;
            return (
              <button
                key={g.key}
                onClick={() => setOpenGroup(g.key)}
                className={`flex min-h-[48px] items-center justify-center gap-2 rounded-xl border px-3 text-sm font-semibold ${g.bg} ${g.border} ${g.text}`}
                title={`Zobrazit úkoly ve sloupci ${g.label}`}
              >
                {g.label}
                <span className="rounded-lg bg-white px-1.5 py-0.5 text-xs font-bold tabular-nums">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400">
          <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Načítám úkoly…
        </div>
      ) : (
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 ${
          (tasksByStatus.ke_kontrole || []).length > 0 ? "xl:grid-cols-3" : "xl:grid-cols-2"
        }`}>
          {COLUMNS.filter((col) => {
            // Telefon: jen vybraný sloupec, kanban se do 390 px nevejde.
            if (uzka) return col.key === vybranySloupec;
            // 29.5.2026: Ke kontrole sloupec skryt když prázdný - drag&drop tam jde i tak (force via DnD na pinned chip nahoře, nebo přes Detail modal).
            if (col.key === "ke_kontrole") return (tasksByStatus.ke_kontrole || []).length > 0;
            return true;
          }).map((col) => (
            <KanbanColumn
              key={col.key}
              column={col}
              tasks={sortTasks(tasksByStatus[col.key] || [])}
              today={today}
              activeTrener={activeTrener}
              userRole={userRole}
              canApprove={canApprove}
              canDelete={canDelete}
              statusChanging={statusChanging}
              onOpenTask={setOpenTaskId}
              onChangeStatus={changeStatus}
              onRequestReview={requestReview}
              onApprove={approveReview}
              onReject={rejectReview}
              onDelete={deleteTask}
              onArchivovat={requestArchive}
              onCreate={createTask}
            />
          ))}
        </div>
      )}

      {/* Modal pro pinned group (Hotovo / Archiv) - kompaktní list úkolů. */}
      {openGroup && (
        <PinnedGroupModal
          group={PINNED_GROUPS.find((g) => g.key === openGroup)}
          tasks={tasksByStatus[openGroup] || []}
          today={today}
          canDelete={canDelete}
          onClose={() => setOpenGroup(null)}
          onOpenTask={(id) => { setOpenGroup(null); setOpenTaskId(id); }}
          onDelete={deleteTask}
          onUnarchive={(taskId) => changeStatus(taskId, "naplanovane")}
        />
      )}

      {openTaskId && (
        <TaskDetailModal
          taskId={openTaskId}
          onClose={() => { setOpenTaskId(null); refetch(); }}
          userRole={userRole}
          canApprove={canApprove}
          canDelete={canDelete}
          /* Vedoucí lokality může úkol přehodit mezi trenéry, které vede. */
          trenerOptions={vedeLokalitu && vedeniTreneri.length
            ? vedeniTreneri.map((jm) => ({ value: jm, label: jm }))
            : null}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Smazat úkol?"
        description={pendingDelete?.title
          ? `Opravdu smazat úkol „${pendingDelete.title}"? Tato akce je nevratná.`
          : "Opravdu smazat tento úkol? Tato akce je nevratná."}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={doDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={!!archivBezKomentare}
        title="Nejdřív napiš komentář"
        description={`Úkol${archivBezKomentare?.title ? ` „${archivBezKomentare.title}"` : ""} nemá žádný komentář od tebe. Otevři ho, napiš do komentáře důvod archivace a pak ho archivuj.`}
        confirmLabel="Rozumím"
        variant="warning"
        onConfirm={() => setArchivBezKomentare(null)}
        onCancel={() => setArchivBezKomentare(null)}
      />

      <ConfirmDialog
        open={!!pendingArchive}
        title="Archivovat úkol?"
        description={`Potvrzuješ, že jsi k úkolu${pendingArchive?.title ? ` „${pendingArchive.title}"` : ""} napsal komentář s důvodem archivace? Úkol zůstane dohledatelný v Archivu.`}
        confirmLabel="Archivovat"
        onConfirm={() => { const id = pendingArchive?.taskId; setPendingArchive(null); if (id) changeStatus(id, "archiv"); }}
        onCancel={() => setPendingArchive(null)}
      />
    </div>
  );
}

// === KANBAN COLUMN ===
function KanbanColumn({ column, tasks, today, activeTrener, userRole, canApprove, canDelete, statusChanging, onOpenTask, onChangeStatus, onRequestReview, onApprove, onReject, onDelete, onArchivovat, onCreate }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [addError, setAddError] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [dropHover, setDropHover] = useState(false);
  const Icon = column.icon;

  // === Kategorie jako accordion (4.6.2026) ===
  // Default sbalené (přehlednost na mobilu), AUTOMATICKY rozbalené sekce s urgentním
  // úkolem (prošlý / dnešní deadline). Ruční stav se pamatuje v localStorage per sloupec.
  const lsKey = `fw40_ukoly_groups_${column.key}`;
  const [openGroups, setOpenGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(lsKey) || "[]")); } catch { return new Set(); }
  });
  const [closedGroups, setClosedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(lsKey + "_closed") || "[]")); } catch { return new Set(); }
  });
  const toggleGroup = (key, isOpen) => {
    const nextOpen = new Set(openGroups);
    const nextClosed = new Set(closedGroups);
    if (isOpen) { nextOpen.delete(key); nextClosed.add(key); }
    else { nextOpen.add(key); nextClosed.delete(key); }
    setOpenGroups(nextOpen); setClosedGroups(nextClosed);
    try {
      localStorage.setItem(lsKey, JSON.stringify([...nextOpen]));
      localStorage.setItem(lsKey + "_closed", JSON.stringify([...nextClosed]));
    } catch { /* private mode */ }
  };

  // 29.5.2026: HTML5 drop target. dragData = task id (set v TaskCard onDragStart).
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dropHover) setDropHover(true);
  };
  const handleDragLeave = () => setDropHover(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDropHover(false);
    const taskId = e.dataTransfer.getData("text/plain");
    if (!taskId) return;
    onChangeStatus(taskId, column.key);
  };

  // Přímé vytvoření + otevření detailu (bez psaní názvu předem) — klik na "+".
  const handleQuickCreateOpen = async () => {
    if (!activeTrener || activeTrener === "Vyber trenéra") { toast("Není vybrán trenér", "error"); return; }
    setAddBusy(true);
    try {
      const r = await onCreate({ nazev: "Nový úkol", pilot: activeTrener, status: column.key, priority: "Medium" });
      if (r?.id && onOpenTask) onOpenTask(String(r.id));
      else if (!r) toast("Vytvoření selhalo (viz console)", "error");
    } catch (e) {
      toast(e.message || "Chyba", "error");
    } finally {
      setAddBusy(false);
    }
  };

  const handleQuickAdd = async () => {
    const t = newTitle.trim();
    if (!t) { setAddError("Název je povinný"); return; }
    if (!activeTrener || activeTrener === "Vyber trenéra") { setAddError("Není vybrán trenér"); return; }
    setAddError(null);
    setAddBusy(true);
    try {
      const r = await onCreate({ nazev: t, pilot: activeTrener, status: column.key, priority: "Medium" });
      if (!r) { setAddError("Vytvoření selhalo (viz console)"); return; }
      setNewTitle("");
      setAdding(false);
      // 29.5.2026 v7: po vytvoření hned otevřít detail aby trenér doplnil popis/deadline/klienta.
      // Backend vrátí { ok: true, id: "X" } — frontend si stáhne čerstvý detail z GET.
      if (r.id && onOpenTask) {
        onOpenTask(String(r.id));
      }
    } catch (e) {
      setAddError(e.message || "Chyba");
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`${column.bg} border rounded-xl flex flex-col min-h-[300px] transition-colors ${dropHover ? "border-brand-500 ring-2 ring-brand-200" : "border-slate-200"}`}
    >
      <div className="px-3 py-2.5 border-b border-slate-200/60 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: column.dot }} />
        <Icon className={`w-4 h-4 ${column.header}`} />
        <span className={`text-sm font-bold ${column.header}`}>{column.label}</span>
        <span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200">{tasks.length}</span>
        <button
          onClick={handleQuickCreateOpen}
          disabled={addBusy}
          title="Přidat úkol (otevře rovnou detail)"
          className="ml-auto p-2 sm:p-1 rounded-xl hover:bg-white/70 text-slate-500 inline-flex items-center justify-center disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          {addBusy ? <RefreshCw className="w-5 h-5 sm:w-4 sm:h-4 animate-spin" /> : <Plus className="w-5 h-5 sm:w-4 sm:h-4" />}
        </button>
      </div>

      {adding && (
        <div className="p-2 border-b border-slate-200/60 bg-white/60">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleQuickAdd(); if (e.key === "Escape") { setAdding(false); setNewTitle(""); setAddError(null); } }}
            placeholder="Název úkolu… (Enter pro uložení)"
            autoFocus
            className="field w-full"
          />
          {addError && (
            <div className="mt-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
              {addError}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <button onClick={handleQuickAdd} disabled={addBusy} className="btn btn-primary">
              {addBusy ? "Ukládám…" : "Vytvořit"}
            </button>
            <button onClick={() => { setAdding(false); setNewTitle(""); setAddError(null); }} className="text-sm sm:text-xs text-slate-500 hover:text-slate-800 px-3 py-2 sm:px-2 sm:py-1">Zrušit</button>
          </div>
        </div>
      )}

      <div className="p-2 space-y-2 flex-1">
        {tasks.length === 0 && !adding ? (
          <div className="text-xs text-slate-400 italic text-center py-8">Žádné úkoly</div>
        ) : (() => {
          const renderCard = (task) => (
            <TaskCard
              key={task.id}
              task={task}
              today={today}
              columnKey={column.key}
              userRole={userRole}
              canApprove={canApprove}
              canDelete={canDelete}
              isChanging={String(statusChanging) === String(task.id)}
              onOpenTask={onOpenTask}
              onChangeStatus={onChangeStatus}
              onRequestReview={onRequestReview}
              onApprove={onApprove}
              onReject={onReject}
              onDelete={onDelete}
              onArchivovat={onArchivovat}
            />
          );
          // Seskupení dle typu úkolu — přehlednost když je úkolů hodně (např. onboarding).
          const groups = [];
          for (const task of tasks) {
            const cat = taskCategory(task);
            let g = groups.find((x) => x.key === cat.key);
            if (!g) { g = { key: cat.key, label: cat.label, items: [] }; groups.push(g); }
            g.items.push(task);
          }
          // Jediná kategorie → plochý seznam bez accordionu.
          if (groups.length <= 1) return tasks.map(renderCard);
          // Accordion: výrazná klikací hlavička, default sbaleno. Auto-rozbalit
          // sekci s urgentním úkolem (prošlý/dnešní deadline), pokud ji user
          // ručně nesbalil. Ruční volba má přednost a pamatuje se.
          return groups.map((g) => {
            const urgentCount = g.items.filter((t) => t.deadline && t.deadline <= today).length;
            const isOpen = openGroups.has(g.key) || (urgentCount > 0 && !closedGroups.has(g.key));
            const catColor = {
              prodlouzeni: "bg-amber-50 border-amber-200 text-amber-900",
              planovani: "bg-sky-50 border-sky-200 text-sky-900",
              dokument: "bg-brand-50 border-brand-200 text-brand-900",
              onboarding: "bg-emerald-50 border-emerald-200 text-emerald-900",
              ostatni: "bg-slate-100 border-slate-200 text-slate-800",
            }[g.key] || "bg-slate-100 border-slate-200 text-slate-800";
            return (
              <div key={g.key} className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key, isOpen)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border font-bold text-sm transition-colors active:brightness-95 ${catColor}`}
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  <span className="truncate">{g.label}</span>
                  <span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-white/70 border border-black/5">{g.items.length}</span>
                  {urgentCount > 0 && (
                    <span className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 ml-auto">
                      {urgentCount} urgentní
                    </span>
                  )}
                </button>
                {isOpen && g.items.map(renderCard)}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

// Kategorie úkolu pro seskupení v Kanbanu (dle tagů). Pořadí = pořadí skupin.
const TASK_CATEGORIES = [
  { key: "prodlouzeni", label: "Prodloužení", test: (s) => /prodlouzeni/.test(s) },
  { key: "planovani", label: "Plánování", test: (s) => /planovani|tyden|mesic/.test(s) },
  { key: "dokument", label: "Dokumenty", test: (s) => /dokument/.test(s) },
  { key: "onboarding", label: "Onboarding aplikace", test: (s) => /onboarding/.test(s) },
];
function taskCategory(task) {
  const tags = String(task.tags || "").toLowerCase();
  return TASK_CATEGORIES.find((c) => c.test(tags)) || { key: "ostatni", label: "Ostatní" };
}

// === TASK CARD ===
// Hezký odkaz jako tlačítko. Popisek = label u markdown odkazu, jinak zkrácená adresa.
function LinkButton({ url, label }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1.5 max-w-full align-bottom text-brand-700 font-semibold bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-xl px-3 py-1.5 my-1 transition-colors"
    >
      <ExternalLink className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </a>
  );
}

// Vykreslí text s podporou **tučné**, markdown odkazů [popisek](url) a holých URL (→ tlačítko).
// stopPropagation u odkazů, aby klik nespustil edit popisu.
// Interní odkaz (relativní /...) → navigace v aplikaci (např. proklik na detail klienta).
function InternalLinkButton({ url, label }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); navigate(url); }}
      className="inline-flex items-center gap-1.5 max-w-full align-bottom text-brand-700 font-semibold bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-xl px-3 py-1 my-0.5 transition-colors"
    >
      <User className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

function LinkifiedText({ text }) {
  const src = String(text || "");
  // [popisek](url) kde url je http(s) NEBO interní /cesta; **tučné**; holá URL.
  const re = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^\s)]+)\)|(\*\*([^*]+)\*\*)|(https?:\/\/[^\s]+)/g;
  const out = [];
  let last = 0, m, i = 0;
  while ((m = re.exec(src))) {
    if (m.index > last) out.push(<React.Fragment key={i++}>{src.slice(last, m.index)}</React.Fragment>);
    if (m[1]) {                       // [popisek](url)
      if (m[2].startsWith("/")) out.push(<InternalLinkButton key={i++} url={m[2]} label={m[1]} />);
      else out.push(<LinkButton key={i++} url={m[2]} label={m[1]} />);
    } else if (m[3]) {                // **tučné**
      out.push(<strong key={i++} className="font-bold text-slate-900">{m[4]}</strong>);
    } else if (m[5]) {                // holá URL
      out.push(<LinkButton key={i++} url={m[5]} label={m[5].replace(/^https?:\/\//, "").replace(/\/$/, "")} />);
    }
    last = re.lastIndex;
  }
  if (last < src.length) out.push(<React.Fragment key={i++}>{src.slice(last)}</React.Fragment>);
  return out;
}

function TaskCard({ task, today, columnKey, userRole, canApprove, canDelete, isChanging, onOpenTask, onChangeStatus, onRequestReview, onApprove, onReject, onDelete, onArchivovat }) {
  const priority = PRIORITY_META[task.priority] || PRIORITY_META.Medium;
  const PriIcon = priority.icon;
  const dline = deadlineLabel(task.deadline, today);
  const reviewDline = task.review_deadline ? deadlineLabel(task.review_deadline, today) : null;
  const isRoutine = task.zdroj === "rutina" || task.zdroj === "auto";
  const isDone = columnKey === "hotovo";
  const needsReview = task.needs_review === true;

  // 29.5.2026: HTML5 drag source.
  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/plain", String(task.id));
    e.dataTransfer.effectAllowed = "move";
  };

  const klientInitials = task.klient_name
    ? task.klient_name.split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase()
    : "";
  const pill = "inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2.5 py-1";

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={() => onOpenTask(task.id)}
      className={`group bg-white rounded-xl border border-slate-200 shadow-sm cursor-grab active:cursor-grabbing transition-all overflow-hidden ${isChanging ? "opacity-50" : "hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300"} ${isDone ? "opacity-75" : ""}`}
      style={{ borderLeft: `3px solid ${priority.border}` }}
    >
      <div className="p-3.5">
        {/* Horní řádek pillů: priorita, kontrola, rutina, deadline */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
          <span className={`${pill} ${priority.bg} ${priority.text}`}>
            <PriIcon className="w-3 h-3" /> {priority.label}
          </span>
          {needsReview && (
            <span className={`${pill} bg-rose-50 text-rose-700`} title="Vyžaduje kontrolu - manager/owner musí schválit">
              <Eye className="w-3 h-3" /> Kontrola
            </span>
          )}
          {isRoutine && (
            <span className={`${pill} bg-plum-50 text-plum-700`}>
              <Repeat className="w-3 h-3" /> Rutina
            </span>
          )}
          {columnKey === "ke_kontrole" && reviewDline ? (
            <span className={`ml-auto ${pill} border ${DEADLINE_TONE[reviewDline.tone]}`} title={`Termín kontroly: ${task.review_deadline}`}>
              <Calendar className="w-3 h-3" /> Schválit {reviewDline.text}
            </span>
          ) : dline && (
            <span className={`ml-auto ${pill} border ${DEADLINE_TONE[dline.tone]}`}>
              <Calendar className="w-3 h-3" /> {dline.text}
            </span>
          )}
        </div>

        {/* Název + popis */}
        <div className={`text-base font-bold text-slate-900 leading-snug demo-sensitive ${isDone ? "line-through text-slate-400" : ""}`}>
          {task.nazev}
        </div>
        {task.popis && (
          <div className="text-sm text-slate-500 mt-1 line-clamp-2 demo-sensitive leading-relaxed"><LinkifiedText text={task.popis} /></div>
        )}

        {/* Spodní pilly: klient, lokalita, vráceno, trenér */}
        <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
          {task.klient_name && (
            <span className={`${pill} bg-brand-50 text-brand-700 pl-0.5`}>
              <span className="w-5 h-5 rounded-full bg-brand-200 text-brand-800 text-[11px] font-bold flex items-center justify-center shrink-0">{klientInitials}</span>
              <span className="truncate max-w-[150px]">{task.klient_name}</span>
            </span>
          )}
          {task.lokalita && (
            <span className={`${pill} bg-slate-100 text-slate-600`}>
              <MapPin className="w-3 h-3" /> {task.lokalita}
            </span>
          )}
          {task.review_rejected_reason && columnKey === "v_procesu" && (
            <span className={`${pill} bg-rose-50 text-rose-700`} title={task.review_rejected_reason}>
              <AlertCircle className="w-3 h-3" /> Vráceno
            </span>
          )}
          <span className={`ml-auto ${pill} bg-slate-100 text-slate-500`}>
            <User className="w-3 h-3" /> <span className="demo-sensitive truncate max-w-[110px]">{task.pilot}</span>
          </span>
        </div>
      </div>

      {/* Akční pilly */}
      {!isDone && (
        <div className="px-3.5 pb-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {columnKey === "naplanovane" && (
            <button
              onClick={() => onChangeStatus(task.id, "v_procesu")}
              disabled={isChanging}
              className="btn btn-sm btn-primary ml-auto"
            >
              <Rocket className="w-3 h-3" /> Začít
            </button>
          )}
          {columnKey === "v_procesu" && (
            <>
              <button
                onClick={() => onChangeStatus(task.id, "naplanovane")}
                disabled={isChanging}
                className="btn btn-sm btn-secondary"
              >
                ← Zpět
              </button>
              <button
                onClick={() => onRequestReview(task.id)}
                disabled={isChanging}
                className="btn btn-sm btn-primary ml-auto"
              >
                <Eye className="w-3 h-3" /> Ke kontrole
              </button>
            </>
          )}
          {columnKey === "ke_kontrole" && (
            canApprove ? (
              <>
                <button
                  onClick={() => onReject(task.id)}
                  disabled={isChanging}
                  className="btn btn-sm btn-secondary"
                >
                  Vrátit
                </button>
                <button
                  onClick={() => onApprove(task.id)}
                  disabled={isChanging}
                  className="btn btn-sm btn-primary ml-auto"
                >
                  <CheckCircle2 className="w-3 h-3" /> Schválit
                </button>
              </>
            ) : (
              <span className={`${pill} bg-slate-100 text-slate-400 italic mx-auto`}>Čeká na schválení</span>
            )
          )}
        </div>
      )}

      {isDone && canDelete && (
        <div className="px-3.5 pb-3 flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onDelete(task.id, task.nazev)}
            title="Smazat"
            className="text-slate-400 hover:text-rose-600 p-0.5"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {/* Manager (13.8.2026): místo mazání jen archivace s povinným komentářem. */}
      {isDone && userRole === "manager" && (
        <div className="px-3.5 pb-3 flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onArchivovat(task.id, task.nazev)}
            title="Archivovat úkol (vyžaduje komentář s důvodem)"
            className="text-slate-400 hover:text-slate-700 p-0.5"
          >
            <Archive className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Rychlé akce přímo v přehledu (bez otvírání detailu). Owner: archiv +
          smazat. Manager (13.8.2026): jen archiv s povinným komentářem, mazat nesmí. */}
      {(userRole === "owner" || userRole === "manager") && !isDone && columnKey !== "archiv" && (
        <div className="px-3.5 pb-3 flex items-center gap-1.5 border-t border-slate-100 pt-2" onClick={(e) => e.stopPropagation()}>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-300 mr-auto">{userRole === "owner" ? "Owner" : "Manager"}</span>
          <button
            onClick={() => (userRole === "manager" ? onArchivovat(task.id, task.nazev) : onChangeStatus(task.id, "archiv"))}
            disabled={isChanging}
            title={userRole === "manager" ? "Archivovat úkol (vyžaduje komentář s důvodem)" : "Archivovat úkol"}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl px-2 py-1 disabled:opacity-50"
          >
            <Archive className="w-3.5 h-3.5" /> Archiv
          </button>
          {userRole === "owner" && (
            <button
              onClick={() => onDelete(task.id, task.nazev)}
              disabled={isChanging}
              title="Smazat úkol"
              className="inline-flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl px-2 py-1 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Smazat
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// === DETAIL MODAL ===
export function TaskDetailModal({ taskId, onClose, userRole, canApprove, canDelete, trenerOptions = null }) {
  // Pod otevřeným detailem nesmí rolovat stránka vzadu, na iOS to vypadá jako zamrznutí.
  useZamekRolovani(true);
  const userEmail = (getCurrentUser()?.email || "").toLowerCase();
  const navigate = useNavigate();
  // Proklik na klienta vede do sekce klientů dle role (manažer má /manager/clients, trenér /trener/klienti).
  const clientsPath = userRole === "manager" ? "/manager/clients" : "/trener/klienti";
  // Přiřazení trenéra smí měnit jen owner/manager/head_coach a jen když dostane seznam (manažerský pohled).
  // 3.9.2026: přehodit trenéra smí i vedoucí lokality (Head Coach Junior) —
  // ta dostane trenerOptions omezené na svou lokalitu, takže víc než ty nemůže.
  const canReassignTrener = Array.isArray(trenerOptions) && trenerOptions.length > 0;
  // Historie změn je owner-only (backend vrací 403 komukoli jinému). Deklarace nahoře,
  // ať se na ni v JSX nesahá dřív, než vznikne (TDZ).
  const jeOwnerHistorie = userRole === "owner";
  const [editingTrener, setEditingTrener] = useState(false);
  // 29.5.2026 v5: Trenér smí editovat popis + podúkoly jen u úkolů které sám vytvořil.
  // Manager+/owner smí všude. Tato proměnná se počítá až po načtení dat (defaultně false).
  const [data, setData] = useState({ task: null, podukoly: [], komentare: [], zminitelni: [], loading: true, error: null });
  const [newComment, setNewComment] = useState("");
  const [newSubtask, setNewSubtask] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingPopis, setEditingPopis] = useState(false);
  const [popisDraft, setPopisDraft] = useState("");
  const [editingNazev, setEditingNazev] = useState(false);
  const [nazevDraft, setNazevDraft] = useState("");
  const [editingKlient, setEditingKlient] = useState(false);
  const [showArchivedComments, setShowArchivedComments] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null); // id rozpracovaného editu
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState(null);

  // Zavřít detail úkolu klávesou Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // 29.5.2026: silent refresh - loading nastaví TRUE jen při prvním načítání (data.task === null).
  // Background reloads (po API chybě, po init etc.) nesmí mazat UI obsah → uživatel by viděl spinner.
  const refresh = useCallback(async (silent = false) => {
    if (!silent) setData((d) => ({ ...d, loading: d.task === null, error: null }));
    try {
      const r = await apiFetch(`/api/trener-ukoly?taskId=${encodeURIComponent(taskId)}`, { credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setData({ task: j.task, podukoly: j.podukoly || [], komentare: j.komentare || [], zminitelni: j.zminitelni || [], loading: false, error: null });
    } catch (e) {
      setData((d) => ({ ...d, loading: false, error: e.message }));
    }
  }, [taskId]);

  useEffect(() => { refresh(); }, [refresh]);

  const t = data.task;
  const podukoly = data.podukoly;
  const komentare = data.komentare;
  // Nabídka @zmínek nad textovým polem komentáře.
  const zminky = useZminky({ value: newComment, onChange: setNewComment, lide: data.zminitelni || [] });

  const doneCount = podukoly.filter((s) => s.hotovo).length;
  const totalCount = podukoly.length;
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  // 29.5.2026 v6: komentáře sort = pinned first, pak ascending vytvoreno.
  // Archivované defaultně skryté, dají se rozkliknout přepínačem.
  const sortedComments = useMemo(() => {
    const visible = komentare.filter((c) => showArchivedComments || !c.archived);
    return [...visible].sort((a, b) => {
      const pa = a.pinned ? 1 : 0, pb = b.pinned ? 1 : 0;
      if (pa !== pb) return pb - pa;
      return new Date(a.vytvoreno) - new Date(b.vytvoreno);
    });
  }, [komentare, showArchivedComments]);
  const archivedCount = komentare.filter((c) => c.archived).length;
  const canDeleteComment = userRole === "owner" || userRole === "manager";

  // === OPTIMISTIC podúkoly + komentáře ===
  const addSubtask = async () => {
    const text = newSubtask.trim();
    if (!text) return;
    const tempId = -Date.now();
    const optimistic = { id: tempId, ukol_id: taskId, text, hotovo: false, poradi: (podukoly[podukoly.length - 1]?.poradi || 0) + 1 };
    setData((d) => ({ ...d, podukoly: [...d.podukoly, optimistic] }));
    setNewSubtask("");
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "addSubtask", id: taskId, text }) });
      const j = await r.json();
      if (!r.ok) throw new Error("Add subtask failed");
      // Nahraď tempId za reálný id ze serveru
      setData((d) => ({ ...d, podukoly: d.podukoly.map((s) => s.id === tempId ? j.subtask : s) }));
    } catch (e) {
      setData((d) => ({ ...d, podukoly: d.podukoly.filter((s) => s.id !== tempId) }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const toggleSubtask = async (subtaskId) => {
    const prev = podukoly;
    setData((d) => ({ ...d, podukoly: d.podukoly.map((s) => s.id === subtaskId ? { ...s, hotovo: !s.hotovo } : s) }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "toggleSubtask", subtaskId }) });
      if (!r.ok) throw new Error("Toggle failed");
    } catch (e) {
      setData((d) => ({ ...d, podukoly: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const deleteSubtask = async (subtaskId) => {
    const prev = podukoly;
    setData((d) => ({ ...d, podukoly: d.podukoly.filter((s) => s.id !== subtaskId) }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "deleteSubtask", subtaskId }) });
      if (!r.ok) throw new Error("Delete failed");
    } catch (e) {
      setData((d) => ({ ...d, podukoly: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const addComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    const tempId = -Date.now();
    const optimistic = {
      id: tempId, ukol_id: taskId, text,
      autor_email: (getCurrentUser()?.email || "").toLowerCase(),
      autor_jmeno: "Ty", autor_role: userRole,
      vytvoreno: new Date().toISOString(),
      edited_at: null, archived: false, pinned: false,
    };
    setData((d) => ({ ...d, komentare: [...d.komentare, optimistic] }));
    setNewComment("");
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "addComment", id: taskId, text }) });
      const j = await r.json();
      if (!r.ok) throw new Error("Comment failed");
      setData((d) => ({ ...d, komentare: d.komentare.map((c) => c.id === tempId ? j.comment : c) }));
    } catch (e) {
      setData((d) => ({ ...d, komentare: d.komentare.filter((c) => c.id !== tempId) }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  // === Edit / archive / pin / delete komentáře ===
  // Optimistic update: lokální komentář se přepíše hned, API běží na pozadí.
  // Při chybě se vrátí na původní hodnoty (uloženo do `prev`).
  const editComment = async (commentId, text) => {
    const prev = data.komentare;
    setData((d) => ({
      ...d,
      komentare: d.komentare.map((c) => c.id === commentId ? { ...c, text, edited_at: new Date().toISOString() } : c),
    }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "editComment", commentId, text }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Edit failed");
      setData((d) => ({ ...d, komentare: d.komentare.map((c) => c.id === commentId ? j.comment : c) }));
    } catch (e) {
      setData((d) => ({ ...d, komentare: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const archiveComment = async (commentId, archived) => {
    const prev = data.komentare;
    setData((d) => ({
      ...d,
      komentare: d.komentare.map((c) => c.id === commentId ? { ...c, archived, archived_at: archived ? new Date().toISOString() : null } : c),
    }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "archiveComment", commentId, archived }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Archive failed");
      setData((d) => ({ ...d, komentare: d.komentare.map((c) => c.id === commentId ? j.comment : c) }));
    } catch (e) {
      setData((d) => ({ ...d, komentare: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const pinComment = async (commentId, pinned) => {
    const prev = data.komentare;
    setData((d) => ({
      ...d,
      komentare: d.komentare.map((c) => c.id === commentId ? { ...c, pinned, pinned_at: pinned ? new Date().toISOString() : null } : c),
    }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "pinComment", commentId, pinned }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Pin failed");
      setData((d) => ({ ...d, komentare: d.komentare.map((c) => c.id === commentId ? j.comment : c) }));
    } catch (e) {
      setData((d) => ({ ...d, komentare: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const deleteComment = async (commentId) => {
    const prev = data.komentare;
    setData((d) => ({ ...d, komentare: d.komentare.filter((c) => c.id !== commentId) }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "deleteComment", commentId }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Delete failed");
    } catch (e) {
      setData((d) => ({ ...d, komentare: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  // Update libovolných polí na úkolu - OPTIMISTIC. Lokální state se updatuje hned,
  // API běží na pozadí. Při chybě se vrátí na původní hodnoty přes refresh.
  const updateFields = async (fields) => {
    const prev = data.task;
    setData((d) => ({ ...d, task: { ...d.task, ...fields } }));
    try {
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ id: taskId, fields }) });
      if (!r.ok) throw new Error("Update selhal");
    } catch (e) {
      // Rollback při chybě
      setData((d) => ({ ...d, task: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const changeStatus = async (newStatus) => {
    const needsReview = t?.needs_review === true;
    // "hotovo" jde přímo, pokud úkol NEvyžaduje kontrolu. Pokud vyžaduje, jen owner/manager
    // schválí (approveReview). "ke_kontrole" = trenér odešle ke kontrole (requestReview).
    let action = null;
    if (newStatus === "ke_kontrole") action = "requestReview";
    else if (newStatus === "hotovo" && needsReview) action = "approveReview";
    if (action === "approveReview" && !canApprove) {
      toast("Tento úkol vyžaduje kontrolu, schválit ho může jen owner, manager nebo head_coach. Pošli ho 'Ke kontrole'.", "error");
      return;
    }
    const prev = data.task;
    setData((d) => ({ ...d, task: { ...d.task, status: newStatus } }));
    try {
      const body = action ? { action, id: taskId } : { id: taskId, status: newStatus };
      const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Změna stavu selhala");
      refresh(true);
    } catch (e) {
      setData((d) => ({ ...d, task: prev }));
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [archivPotvrzeni, setArchivPotvrzeni] = useState(false); // manager potvrzuje archivaci
  const [archivUpozorneni, setArchivUpozorneni] = useState(false); // manager: chybi komentar
  const deleteTask = () => setConfirmDelete(true);

  // 7.8.2026: kopie odkazu na ukol — stejna vychytavka jako na Operations boardu.
  // Pouzivame aktualni pathname (trener /trener/ukoly, manazer /manager/ukoly) + ?open=<id>,
  // oba boardy deep-link ?open podporuji.
  const kopirujOdkaz = async () => {
    const url = `${window.location.origin}${window.location.pathname}?open=${taskId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast("Odkaz na \u00fakol zkop\u00edrov\u00e1n.", "success");
    } catch (_) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        toast("Odkaz na \u00fakol zkop\u00edrov\u00e1n.", "success");
      } catch (_e) { toast("Zkop\u00edrov\u00e1n\u00ed odkazu selhalo.", "error"); }
    }
  };
  const doDeleteTask = async () => {
    setConfirmDelete(false);
    try {
      await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "delete", id: String(taskId) }) });
      onClose();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-2xl shadow-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {data.loading ? (
          <div className="p-8 text-center text-slate-400">
            <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
            Načítám…
          </div>
        ) : data.error ? (
          <div className="p-6 text-rose-700">Chyba: {data.error}</div>
        ) : !t ? (
          <div className="p-6 text-slate-500">Úkol nenalezen.</div>
        ) : (
          <>
            {/* 5.9.2026: hlavička podle stejného vzoru jako detail v Operations —
                gradient, šipka zpět, ikonové akce, název a pod ním štítky. */}
            <div className="shrink-0 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 pb-3 pt-3 sm:px-5">
              <div className="mb-2 flex items-center gap-2">
                <button onClick={onClose} className="btn-ikona btn-ikona-tmava" aria-label="Zpět" title="Zpět">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="min-w-0 flex-1" />
                <button onClick={kopirujOdkaz} className="btn-ikona btn-ikona-tmava" aria-label="Zkopírovat odkaz" title="Zkopírovat odkaz na úkol">
                  <Link2 className="h-5 w-5" />
                </button>
                {(() => {
                  // Hotovo jedním klikem — jen když jsou všechny podúkoly splněné.
                  if (["hotovo", "archiv"].includes(t.status)) return null;
                  if (t.needs_review === true && !canApprove) return null;
                  const muze = totalCount === 0 || doneCount === totalCount;
                  return (
                    <button
                      onClick={() => { if (muze && !busy) changeStatus("hotovo"); }}
                      disabled={!muze || busy}
                      aria-label="Označit úkol jako hotový"
                      title={muze ? "Označit úkol jako hotový" : "Lze až po splnění všech podúkolů"}
                      className="btn-ikona btn-ikona-tmava"
                    >
                      <CheckCircle2 className="h-5 w-5" />
                    </button>
                  );
                })()}
              </div>
              {(() => {
                const canEdit = canApprove || (t.created_by && t.created_by.toLowerCase() === userEmail);
                if (editingNazev && canEdit) {
                  return (
                    <input
                      value={nazevDraft}
                      autoFocus
                      onChange={(e) => setNazevDraft(e.target.value)}
                      onBlur={() => { const v = nazevDraft.trim(); if (v && v !== t.nazev) updateFields({ nazev: v }); setEditingNazev(false); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } if (e.key === "Escape") { setNazevDraft(t.nazev || ""); setEditingNazev(false); } }}
                      className="w-full rounded-xl bg-white/10 px-2 py-1 font-serif text-[21px] font-bold leading-tight text-white outline-none"
                    />
                  );
                }
                return (
                  <div
                    onClick={() => { if (canEdit) { setNazevDraft(t.nazev || ""); setEditingNazev(true); } }}
                    title={canEdit ? "Klikni pro úpravu názvu" : undefined}
                    className={`font-serif text-[21px] font-bold leading-tight text-white demo-sensitive ${canEdit ? "cursor-text rounded-lg px-1 -mx-1 hover:bg-white/10" : ""}`}
                  >
                    {t.nazev}
                  </div>
                );
              })()}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white">{(COLUMNS.find((c) => c.key === t.status) || {}).label || t.status}</span>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${
                  t.priority === "High" ? "bg-rose-500" : t.priority === "Low" ? "bg-white/15" : "bg-amber-500"
                }`}>
                  {{ High: "Vysoká", Medium: "Střední", Low: "Nízká" }[t.priority] || "Střední"}
                </span>
                {t.deadline && (() => {
                  const dl = deadlineLabel(t.deadline, todayIso());
                  return (
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${
                      dl?.tone === "overdue" ? "bg-rose-500" : dl?.tone === "today" ? "bg-amber-500" : "bg-white/15"
                    }`}>
                      {dl?.text || t.deadline}
                    </span>
                  );
                })()}
                {t.zdroj === "rutina" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white">
                    <Repeat className="h-3 w-3" /> Rutina
                  </span>
                )}
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold text-white">#{t.id}</span>
              </div>
            </div>

            {/* 29.5.2026 v8: na mobile (pod lg) jeden scroll na celý modal (overflow-y-auto na parent).
                Na desktopu dva sloupce, každý vlastní scroll. Bez toho na mobile scroll mezi
                sekcemi se "hyká" — vnitřní scroll ne vždy bubble nahoru. */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain lg:overflow-hidden lg:grid lg:grid-cols-3">
              <div className="min-w-0 lg:col-span-2 lg:overflow-y-auto p-4 sm:p-5 space-y-5 lg:border-r lg:border-slate-200 break-words">
                {/* 29.5.2026 v5: canEditContent = owner/manager/head_coach VŽDY, trenér jen u vlastních úkolů.
                    Když mu úkol někdo zadal, vidí jen read-only popis + podúkoly (může je odškrtávat ale
                    ne editovat text). */}
                {/* Úkol k dokumentu → přímý proklik na dokument (rutina_id dokument_<slug>_...) */}
                {(() => {
                  const m = String(t.rutina_id || "").match(/^dokument_(.+?)_(?:v\d+_|demo)/);
                  if (!m) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => navigate(`/trener/dokumenty/${encodeURIComponent(m[1])}`)}
                      className="btn btn-primary w-full"
                    >
                      <BookOpen className="w-4 h-4" />
                      Otevřít dokument k prostudování
                    </button>
                  );
                })()}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Popis</div>
                  {editingPopis && (canApprove || (t.created_by && t.created_by.toLowerCase() === userEmail)) ? (
                    <textarea
                      value={popisDraft}
                      onChange={(e) => setPopisDraft(e.target.value)}
                      onBlur={() => {
                        if (popisDraft !== (t.popis || "")) {
                          // Optimistic update - updateFields nastaví lokální task hned, API běží na pozadí.
                          updateFields({ popis: popisDraft });
                        }
                        setEditingPopis(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") { setPopisDraft(t.popis || ""); setEditingPopis(false); }
                      }}
                      rows={Math.max(3, popisDraft.split("\n").length + 1)}
                      autoFocus
                      placeholder="Stručný popis úkolu… (Esc zruší, klik jinam uloží)"
                      className="field w-full leading-relaxed"
                    />
                  ) : t.popis ? (
                    (() => {
                      const canEdit = canApprove || (t.created_by && t.created_by.toLowerCase() === userEmail);
                      return canEdit ? (
                        <div
                          onClick={() => { setPopisDraft(t.popis); setEditingPopis(true); }}
                          title="Klikni pro úpravu"
                          className="text-base text-slate-700 whitespace-pre-wrap leading-relaxed p-3 -mx-1 rounded-xl hover:bg-slate-50 border-2 border-transparent hover:border-slate-200 cursor-text transition-colors"
                        >
                          <LinkifiedText text={t.popis} />
                        </div>
                      ) : (
                        <div className="text-base text-slate-700 whitespace-pre-wrap leading-relaxed p-3 -mx-1 bg-slate-50 rounded-xl border-2 border-slate-100">
                          <LinkifiedText text={t.popis} />
                        </div>
                      );
                    })()
                  ) : (canApprove || (t.created_by && t.created_by.toLowerCase() === userEmail)) ? (
                    <button
                      onClick={() => { setPopisDraft(""); setEditingPopis(true); }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 text-sm font-semibold rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Přidat popis úkolu
                    </button>
                  ) : (
                    <div className="text-xs text-slate-400 italic p-3 -mx-1 bg-slate-50 rounded-xl border border-slate-100">
                      Bez popisu. Tento úkol ti někdo zadal, popis upravit nemůžeš.
                    </div>
                  )}
                </div>
                {t.review_rejected_reason && t.status === "v_procesu" && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-1 inline-flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> Vráceno k přepracování
                    </div>
                    <div className="text-sm text-rose-800">{t.review_rejected_reason}</div>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Podúkoly</div>
                    {totalCount > 0 && <span className="text-xs text-slate-400">{doneCount} z {totalCount} hotových</span>}
                    {totalCount > 0 && (
                      <div className="flex-1 max-w-[120px] h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
                      </div>
                    )}
                  </div>
                  {totalCount > 0 && (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 mb-2">
                      Odškrtnutím potvrzuješ, že jsi daný bod skutečně splnil. Úkol nelze odeslat ke kontrole ani označit jako hotový, dokud nejsou odškrtnuté všechny body.
                    </div>
                  )}
                  {(() => {
                    // 29.5.2026 v5: Trenér smí měnit (přidat/smazat) podúkoly jen u VLASTNÍCH úkolů.
                    // Odškrtnout / zaškrtnout (toggle) smí všude, protože to je výkon práce.
                    const canEditContent = canApprove || (t.created_by && t.created_by.toLowerCase() === userEmail);
                    return (
                      <div className="space-y-1.5">
                        {podukoly.map((s) => (
                          <div key={s.id} className="flex items-start gap-3 group rounded-xl px-2 py-2 hover:bg-slate-50 transition-colors">
                            <button onClick={() => toggleSubtask(s.id)} disabled={busy} className="text-slate-400 hover:text-emerald-600 mt-0.5 shrink-0">
                              {s.hotovo
                                ? <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                                : <div className="w-6 h-6 border-2 border-slate-300 rounded-lg" />}
                            </button>
                            <span className={`text-base flex-1 leading-snug ${s.hotovo ? "line-through text-slate-400" : "text-slate-800"}`}>{s.text}</span>
                            {canEditContent && (
                              <button onClick={() => deleteSubtask(s.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 p-1 shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        {canEditContent ? (
                          <div className="flex items-center gap-2 pt-1">
                            <Plus className="w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              value={newSubtask}
                              onChange={(e) => setNewSubtask(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") addSubtask(); }}
                              placeholder="Přidat podúkol…"
                              className="flex-1 text-sm border-none outline-none placeholder:text-slate-400 bg-transparent"
                            />
                          </div>
                        ) : podukoly.length === 0 ? (
                          <div className="text-xs text-slate-400 italic py-1">Bez podúkolů.</div>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Komentáře <span className="text-slate-400 font-normal">, {komentare.length - archivedCount}</span>
                    </div>
                    {archivedCount > 0 && (
                      <button
                        onClick={() => setShowArchivedComments((v) => !v)}
                        className="text-[11px] font-medium text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                      >
                        <Archive className="w-3 h-3" />
                        {showArchivedComments ? "Skrýt archivované" : `Zobrazit archivované (${archivedCount})`}
                      </button>
                    )}
                  </div>
                  <div className="space-y-3 mb-3">
                    {sortedComments.map((c) => (
                      <CommentItem
                        key={c.id}
                        comment={c}
                        userEmail={userEmail}
                        userRole={userRole}
                        canDeleteComment={canDeleteComment}
                        isEditing={editingCommentId === c.id}
                        onStartEdit={() => setEditingCommentId(c.id)}
                        onCancelEdit={() => setEditingCommentId(null)}
                        onSaveEdit={async (text) => { await editComment(c.id, text); setEditingCommentId(null); }}
                        onArchive={() => archiveComment(c.id, !c.archived)}
                        onPin={() => pinComment(c.id, !c.pinned)}
                        onDelete={() => setConfirmDeleteCommentId(c.id)}
                      />
                    ))}
                    {sortedComments.length === 0 && <div className="text-xs text-slate-400 italic">Žádné komentáře.</div>}
                  </div>
                  {/* 29.5.2026 v3: Enter (bez modifikátoru) odešle, Shift+Enter nový řádek.
                      Textarea má větší default + auto-grow podle počtu řádků (max 8). */}
                  <div className={`border-2 rounded-xl p-3 flex items-end gap-2 transition-colors ${
                    newComment.trim() ? "border-brand-300 bg-brand-50/20" : "border-slate-200"
                  }`}>
                    <div className="relative flex-1 min-w-0 flex">
                      <textarea
                        ref={zminky.ref}
                        value={newComment}
                        onChange={zminky.onChangeVstup}
                        onBlur={zminky.onBlur}
                        onKeyDown={(e) => {
                          // Nabídka zmínek má přednost: Enter v ní vybírá jméno,
                          // ne odesílá komentář.
                          if (zminky.onKeyDown(e)) return;
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (newComment.trim()) addComment();
                          }
                        }}
                        rows={Math.min(8, Math.max(2, newComment.split("\n").length + 1))}
                        placeholder="Napsat komentář… (@ označí člověka, Enter odešle)"
                        className="flex-1 text-base border-none outline-none resize-none bg-transparent leading-relaxed placeholder:text-slate-400"
                      />
                      {zminky.nabidka}
                    </div>
                    <button
                      onClick={addComment}
                      disabled={busy || !newComment.trim()}
                      className="btn btn-primary"
                    >
                      <Send className="w-4 h-4" /> Odeslat
                    </button>
                  </div>
                </div>

                {/* Historie změn (7.8.2026) — kdo a kdy posunul deadline, přehodil pilota…
                    Jen owner: je to dohledávání "kdo mi to změnil", backend hlídá totéž. */}
                <UkolHistorie enabled={jeOwnerHistorie} endpoint={`/api/trener-ukoly?historie=1&id=${encodeURIComponent(taskId)}`} />
              </div>

              <div className="min-w-0 lg:overflow-y-auto p-4 space-y-4 bg-slate-50/50 border-t border-slate-200 lg:border-t-0">
                {/* 29.5.2026 v4: Status list filtruje volby pro trenéra:
                    - Pokud needs_review=true a status != hotovo, schovat Hotovo (musí přes Ke kontrole).
                    - Pokud úkol vytvořil sám trenér, schovat Archiv (jen owner/manager může archivovat vlastní úkoly trenéra).
                    Owner/manager/head_coach vidí všechny volby. */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Status</div>
                  {(() => {
                    const needsReview = t.needs_review === true;
                    const allDone = totalCount === 0 || doneCount === totalCount;
                    const remaining = totalCount - doneCount;
                    return (
                      <>
                        <div className="flex flex-col gap-1">
                          {[
                            ...COLUMNS,
                            { key: "hotovo", label: "Hotovo", dot: "#0F6E56" },
                            { key: "archiv", label: "Archiv", dot: "#5F5E5A" },
                          ].filter((c) => {
                            const isTrener = userRole === "trener";
                            const createdBySelf = t.created_by && t.created_by.toLowerCase() === userEmail;
                            // "Ke kontrole" má smysl jen u úkolů, co vyžadují kontrolu — jinak skryj všem.
                            if (c.key === "ke_kontrole" && !needsReview) return false;
                            // Archiv: POUZE trenér u úkolu, který si vytvořil sám pro sebe. Owner/manager nearchivuje (maže).
                            if (c.key === "archiv" && !(isTrener && createdBySelf)) return false;
                            if (canApprove) return true; // owner/manager/head_coach vidí zbytek
                            if (isTrener && c.key === "hotovo" && needsReview && t.status !== "hotovo") return false;
                            return true;
                          }).map((c) => {
                            const active = t.status === c.key;
                            // Hotovo / Ke kontrole zablokované dokud nejsou odškrtnuté všechny podúkoly.
                            const blockedByChecklist = (c.key === "hotovo" || c.key === "ke_kontrole") && !allDone;
                            return (
                              <button
                                key={c.key}
                                onClick={() => { if (!active && !blockedByChecklist) changeStatus(c.key); }}
                                disabled={busy || blockedByChecklist}
                                title={blockedByChecklist ? "Lze až po splnění všech podúkolů" : undefined}
                                className={`w-full text-left text-base px-3 py-2.5 rounded-xl inline-flex items-center gap-2.5 border transition-colors ${
                                  active ? "font-bold border-transparent"
                                  : blockedByChecklist ? "text-slate-300 cursor-not-allowed border-transparent"
                                  : "text-slate-600 border-transparent hover:bg-white hover:border-slate-200"
                                }`}
                                style={active ? { background: `${c.dot}1F`, color: c.dot, borderColor: `${c.dot}55` } : undefined}
                              >
                                <span className="w-3 h-3 rounded-full ring-2 ring-white shadow-sm" style={{ background: c.dot, opacity: blockedByChecklist ? 0.4 : 1 }} />
                                {c.label}
                                {active && <CheckCircle2 className="w-4 h-4 ml-auto" />}
                                {blockedByChecklist && <Lock className="w-3.5 h-3.5 ml-auto text-slate-300" />}
                              </button>
                            );
                          })}
                        </div>
                        {!allDone && (
                          <div className="mt-2 text-xs text-slate-500 bg-slate-100 rounded-xl px-3 py-2">
                            Hotovo bude možné až po odškrtnutí všech podúkolů (zbývá {remaining}).
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {!canApprove && userRole === "trener" && t.needs_review && t.status !== "hotovo" && (
                    <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                      <Eye className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                      Úkol vyžaduje kontrolu - Hotovo zvládne až manažer po schválení.
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Klient</div>
                    {t.klient_name && !editingKlient && (
                      <button onClick={() => setEditingKlient(true)} className="text-[11px] text-brand-600 hover:text-brand-800">Změnit</button>
                    )}
                  </div>
                  {editingKlient ? (
                    <KlientPicker
                      currentName={t.klient_name}
                      onPick={async (klient) => {
                        setEditingKlient(false);
                        await updateFields({ klient_id: klient?.notion_id || "", klient_name: klient?.klient_name || "", lokalita: klient?.lokalita || t.lokalita });
                      }}
                      onCancel={() => setEditingKlient(false)}
                    />
                  ) : t.klient_name ? (
                    <div className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl">
                      <div className="w-7 h-7 rounded-full bg-rose-100 text-rose-800 flex items-center justify-center text-xs font-bold">
                        {t.klient_name.split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase()}
                      </div>
                      <button
                        onClick={() => navigate(`${clientsPath}?open=${encodeURIComponent(t.klient_name)}`)}
                        className="flex-1 min-w-0 text-left group"
                        title="Otevřít detail klienta"
                      >
                        <div className="text-sm font-bold text-slate-800 truncate group-hover:text-brand-700 group-hover:underline flex items-center gap-1">
                          {t.klient_name} <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-brand-600" />
                        </div>
                        {t.lokalita && <div className="text-[11px] text-slate-500">{t.lokalita}</div>}
                      </button>
                      <button onClick={() => updateFields({ klient_id: "", klient_name: "", lokalita: "" })} className="text-slate-300 hover:text-rose-600 p-1" title="Odstranit klienta">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingKlient(true)}
                      className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-xl border border-dashed border-slate-300 text-slate-600 hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Týká se klienta
                    </button>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Trenér</div>
                    {canReassignTrener && !editingTrener && (
                      <button onClick={() => setEditingTrener(true)} className="text-[11px] text-brand-600 hover:text-brand-800">Změnit</button>
                    )}
                  </div>
                  {canReassignTrener && editingTrener ? (
                    <select
                      autoFocus
                      value={t.pilot || ""}
                      onChange={(e) => { const v = e.target.value; setEditingTrener(false); if (v && v !== t.pilot) updateFields({ pilot: v }); }}
                      className="field w-full"
                    >
                      {!trenerOptions.some((o) => o.value === t.pilot) && t.pilot && <option value={t.pilot}>{t.pilot}</option>}
                      {trenerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <div className="text-sm font-semibold text-slate-800 demo-sensitive flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-slate-400" />{t.pilot || "nepřiřazen"}
                    </div>
                  )}
                </div>

                {/* 29.5.2026 v6: Priorita + Deadline jsou součást "obsahu úkolu" - trenér je nesmí
                    měnit u úkolu který mu někdo zadal. Zobrazí se jako read-only. */}
                {(() => {
                  const canEditMeta = canApprove || (t.created_by && t.created_by.toLowerCase() === userEmail);
                  return (
                    <>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Priorita</div>
                        {canEditMeta ? (
                          <div className="grid grid-cols-3 gap-2">
                            {["High", "Medium", "Low"].map((p) => {
                              const meta = PRIORITY_META[p];
                              const Icon = meta.icon;
                              const active = t.priority === p;
                              const solid = { High: "bg-red-600 text-white border-red-600 shadow-sm", Medium: "bg-amber-500 text-white border-amber-500 shadow-sm", Low: "bg-slate-500 text-white border-slate-500 shadow-sm" }[p];
                              return (
                                <button
                                  key={p}
                                  onClick={() => !active && updateFields({ priority: p })}
                                  disabled={busy}
                                  className={`text-xs font-bold px-2 py-2 rounded-xl border-2 inline-flex items-center justify-center gap-1.5 transition-colors ${active ? solid : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50"}`}
                                >
                                  <Icon className="w-3.5 h-3.5" /> {meta.label}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          (() => {
                            const meta = PRIORITY_META[t.priority] || PRIORITY_META.Medium;
                            const Icon = meta.icon;
                            return (
                              <div className={`text-[11px] font-bold px-2 py-1.5 rounded border inline-flex items-center gap-1 ${meta.bg} ${meta.text} border-current`}>
                                <Icon className="w-3 h-3" /> {meta.label}
                              </div>
                            );
                          })()
                        )}
                      </div>

                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Deadline</div>
                        {canEditMeta ? (
                          <input
                            type="date"
                            value={t.deadline || ""}
                            onChange={(e) => updateFields({ deadline: e.target.value })}
                            disabled={busy}
                            className="field w-full"
                          />
                        ) : (
                          <div className="text-sm text-slate-700 px-2 py-1.5 bg-slate-50 border border-slate-100 rounded-xl">
                            {t.deadline || "Bez termínu"}
                          </div>
                        )}
                        {!canEditMeta && (
                          <div className="text-[11px] text-slate-500 mt-1">Termín nastavil zadavatel, nelze měnit.</div>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* 29.5.2026 v2: Segmented [Ne] [Ano] - jasně vidět co je aktivní + co bude po kliku.
                    needs_review = true → trenér musí přes Ke kontrole; manager/head_coach může schválit. */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Vyžaduje kontrolu</div>
                  {canApprove ? (
                    <>
                      <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl">
                        <button
                          onClick={() => t.needs_review && updateFields({ needs_review: false })}
                          className={`text-sm font-bold py-1.5 rounded-lg transition-colors ${
                            !t.needs_review ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          Ne
                        </button>
                        <button
                          onClick={() => !t.needs_review && updateFields({ needs_review: true })}
                          className={`text-sm font-bold py-1.5 rounded-lg inline-flex items-center justify-center gap-1 transition-colors ${
                            t.needs_review ? "bg-white text-pink-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Ano
                        </button>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                        {t.needs_review
                          ? "Trenér ho musí poslat ke kontrole - schvalovatel ho schválí do Hotovo."
                          : "Trenér ho po dokončení rovnou označí jako Hotovo."}
                      </div>
                    </>
                  ) : (
                    <div className={`text-sm px-3 py-2 rounded-xl border ${t.needs_review ? "bg-pink-50 text-pink-800 border-pink-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      {t.needs_review
                        ? "Pošli ke kontrole až dokončíš - manažer schválí."
                        : "Můžeš rovnou označit jako Hotovo."}
                    </div>
                  )}
                </div>

                {t.review_deadline && t.status === "ke_kontrole" && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Termín kontroly</div>
                    <div className="text-sm text-slate-700">{t.review_deadline}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">5 dní od odevzdání</div>
                  </div>
                )}

                {t.review_assignee && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Schvalovatel</div>
                    <div className="text-sm text-slate-700">{t.review_assignee}</div>
                  </div>
                )}

                {t.status === "ke_kontrole" && canApprove && (
                  <div className="pt-2 border-t border-slate-200 space-y-2">
                    <button onClick={() => changeStatus("hotovo")} disabled={busy} className="btn btn-primary w-full">
                      <CheckCircle2 className="w-4 h-4" /> Schválit → Hotovo
                    </button>
                    <button
                      onClick={async () => {
                        const reason = prompt("Důvod vrácení (vidí trenér):");
                        if (!reason || !reason.trim()) return;
                        setBusy(true);
                        try {
                          const r = await apiFetch("/api/trener-ukoly", { method: "POST", body: JSON.stringify({ action: "rejectReview", id: taskId, reason: reason.trim() }) });
                          if (!r.ok) throw new Error("Reject failed");
                          refresh();
                        } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
                        finally { setBusy(false); }
                      }}
                      disabled={busy}
                      className="w-full text-sm font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 px-3 py-2 rounded-xl"
                    >
                      Vrátit k přepracování
                    </button>
                  </div>
                )}

                {/* 29.5.2026 v7: Trenér smí smazat svůj vlastní úkol (created_by === self).
                    Owner/manager smí smazat kterýkoli (canDelete=true z parent). */}
                {(canDelete || (t.created_by && t.created_by.toLowerCase() === userEmail)) && (
                  <div className="pt-2 border-t border-slate-200">
                    <button onClick={deleteTask} className="w-full text-sm text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-xl inline-flex items-center justify-center gap-1.5 min-h-[44px] sm:min-h-0">
                      <Trash2 className="w-4 h-4" /> Smazat úkol
                    </button>
                  </div>
                )}
                {/* 13.7.2026 (owner) + 13.8.2026 (manager): archivace z detailu.
                    Manager musí mít u úkolu komentář s důvodem a archivaci potvrdit. */}
                {(userRole === "owner" || userRole === "manager") && t.status !== "archiv" && (
                  <button
                    onClick={async () => {
                      if (userRole === "manager") {
                        const mojeZive = (data.komentare || []).filter((c) => !c.archived && String(c.autor_email || "").toLowerCase() === userEmail);
                        if (mojeZive.length === 0) { setArchivUpozorneni(true); return; }
                        setArchivPotvrzeni(true);
                        return;
                      }
                      await changeStatus("archiv"); onClose && onClose();
                    }}
                    className="w-full text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-50 px-3 py-2 rounded-xl inline-flex items-center justify-center gap-1.5"
                    title="Přesunout do Archivu. Úkol zmizí z přehledu, ale zůstane dohledatelný."
                  >
                    Archivovat úkol
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Smazat úkol?"
        description={t?.nazev ? `Opravdu smazat úkol „${t.nazev}"? Tato akce je nevratná.` : "Opravdu smazat tento úkol? Tato akce je nevratná."}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={doDeleteTask}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        open={archivUpozorneni}
        title="Nejdřív napiš komentář"
        description="Úkol nemá žádný komentář od tebe. Napiš do komentářů důvod archivace a pak úkol archivuj."
        confirmLabel="Rozumím"
        variant="warning"
        onConfirm={() => setArchivUpozorneni(false)}
        onCancel={() => setArchivUpozorneni(false)}
      />

      <ConfirmDialog
        open={archivPotvrzeni}
        title="Archivovat úkol?"
        description="Potvrzuješ, že jsi k úkolu napsal komentář s důvodem archivace? Úkol zůstane dohledatelný v Archivu."
        confirmLabel="Archivovat"
        onConfirm={async () => { setArchivPotvrzeni(false); await changeStatus("archiv"); onClose && onClose(); }}
        onCancel={() => setArchivPotvrzeni(false)}
      />

      <ConfirmDialog
        open={confirmDeleteCommentId !== null}
        title="Smazat komentář?"
        description="Komentář bude trvale odstraněn. Tato akce je nevratná. Pokud ho chceš jen schovat, použij raději archivaci."
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={async () => {
          const id = confirmDeleteCommentId;
          setConfirmDeleteCommentId(null);
          await deleteComment(id);
        }}
        onCancel={() => setConfirmDeleteCommentId(null)}
      />
    </div>
  );
}

// === CommentItem ===
// Render jednoho komentáře včetně hover menu (Pin/Archive/Edit/Delete).
// Pravidla:
//   - Pin: kdokoli
//   - Archive: kdokoli (toggle)
//   - Edit: jen autor a jen do 60 minut od vytvoření (nebo owner/manager kdykoli)
//   - Delete: jen owner nebo manager (HARD)
// Edit režim: textarea s Enter=save, Esc=cancel, Save tlačítko + Zrušit.
function CommentItem({ comment: c, userEmail, userRole, canDeleteComment, isEditing, onStartEdit, onCancelEdit, onSaveEdit, onArchive, onPin, onDelete }) {
  const [draft, setDraft] = useState(c.text || "");
  useEffect(() => { setDraft(c.text || ""); }, [c.text, isEditing]);

  const isOwnerOrMgr = userRole === "owner" || userRole === "manager";
  const isAuthor = String(c.autor_email || "").toLowerCase() === userEmail;
  const createdAt = c.vytvoreno ? new Date(c.vytvoreno) : null;
  const ageMinutes = createdAt ? (Date.now() - createdAt.getTime()) / 60000 : Infinity;
  // Autor smí editovat prvních 60 minut, manager/owner kdykoli.
  const canEdit = (isAuthor && ageMinutes <= 60) || isOwnerOrMgr;
  const archived = c.archived === true;
  const pinned = c.pinned === true;

  const initials = (c.autor_jmeno || c.autor_email || "?").split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase().substring(0, 2);

  return (
    <div className={`group flex gap-2.5 rounded-xl -mx-1 px-1 py-1 transition-colors ${archived ? "opacity-60" : ""} ${pinned ? "bg-amber-50/40" : "hover:bg-slate-50/60"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${pinned ? "bg-amber-100 text-amber-800" : "bg-brand-100 text-brand-800"}`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
          <span className="text-xs font-bold text-slate-800">{c.autor_jmeno || c.autor_email}</span>
          {c.autor_role && <span className="text-[11px] uppercase tracking-wider text-slate-400">{c.autor_role}</span>}
          <span className="text-[11px] text-slate-400">{createdAt ? formatDateTime(createdAt) : ""}</span>
          {c.edited_at && <span className="text-[11px] text-slate-400 italic">(upraveno)</span>}
          {pinned && (
            <span className="text-[11px] font-bold text-amber-700 inline-flex items-center gap-0.5">
              <Pin className="w-3 h-3" /> Připnuto
            </span>
          )}
          {archived && (
            <span className="text-[11px] font-bold text-slate-500 inline-flex items-center gap-0.5">
              <Archive className="w-3 h-3" /> Archivováno
            </span>
          )}
          {/* Hover akce - ml-auto + opacity-0 group-hover:opacity-100 */}
          {!isEditing && (
            <div className="ml-auto opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity inline-flex items-center gap-0.5">
              <button
                onClick={onPin}
                title={pinned ? "Odepnout" : "Připnout"}
                className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-amber-700 min-h-[44px] sm:min-h-0"
              >
                {pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={onArchive}
                title={archived ? "Vrátit z archivu" : "Archivovat"}
                className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800 min-h-[44px] sm:min-h-0"
              >
                {archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
              </button>
              {canEdit && (
                <button
                  onClick={onStartEdit}
                  title={isAuthor && !isOwnerOrMgr ? `Upravit (zbývá ${Math.max(0, Math.round(60 - ageMinutes))} min)` : "Upravit"}
                  className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-brand-700 min-h-[44px] sm:min-h-0"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
              {canDeleteComment && (
                <button
                  onClick={onDelete}
                  title="Smazat komentář"
                  className="p-1 rounded hover:bg-rose-100 text-slate-500 hover:text-rose-700 min-h-[44px] sm:min-h-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        {isEditing ? (
          <div className="mt-1">
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") { e.preventDefault(); onCancelEdit(); }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (draft.trim() && draft.trim() !== c.text) onSaveEdit(draft.trim());
                  else onCancelEdit();
                }
              }}
              rows={Math.min(8, Math.max(2, draft.split("\n").length + 1))}
              className="w-full text-sm border-2 border-brand-300 bg-brand-50/20 rounded p-2 outline-none resize-none leading-relaxed"
            />
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={() => {
                  if (draft.trim() && draft.trim() !== c.text) onSaveEdit(draft.trim());
                  else onCancelEdit();
                }}
                disabled={!draft.trim()}
                className="btn btn-primary"
              >
                Uložit
              </button>
              <button
                onClick={onCancelEdit}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1"
              >
                Zrušit
              </button>
              <span className="text-[11px] text-slate-400 ml-auto">Enter uloží, Esc zruší</span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-700 whitespace-pre-wrap break-words"><ZminkaText text={c.text} /></div>
        )}
      </div>
    </div>
  );
}




// === KLIENT PICKER ===
// Search input + dropdown autocompletu. Načte /api/clients?light=ids (cache 10 min).
// === PINNED GROUP MODAL ===
// Klik na Hotovo/Archiv pill → modal s listem úkolů ve sloupci. Kompaktní view, klik na řádek otevře detail.
function PinnedGroupModal({ group, tasks, today, canDelete, onClose, onOpenTask, onDelete, onUnarchive }) {
  if (!group) return null;
  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className={`px-5 py-3 border-b border-slate-200 flex items-center gap-3 ${group.bg}`}>
          <span className="w-2 h-2 rounded-full" style={{ background: group.dot }} />
          <div className="flex-1">
            <div className={`text-base font-bold ${group.text}`}>{group.emoji} {group.label}</div>
            <div className="text-xs text-slate-500">{tasks.length} úkolů</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-1.5 flex-1">
          {tasks.length === 0 && (
            <div className="text-center py-10 text-slate-400">
              <div className="text-4xl mb-2 opacity-40">{group.emoji}</div>
              <div className="text-sm">Žádné úkoly v {group.label}.</div>
            </div>
          )}
          {tasks.map((t) => {
            const priority = PRIORITY_META[t.priority] || PRIORITY_META.Medium;
            return (
              <div key={t.id} className="bg-white border border-slate-200 rounded-xl px-3 py-2 hover:border-slate-300 hover:bg-slate-50 transition-colors flex items-center gap-3">
                <div className="w-1 h-8 rounded-full" style={{ background: priority.border }} />
                <button onClick={() => onOpenTask(t.id)} className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{t.nazev}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                    <span className="demo-sensitive">{t.pilot}</span>
                    {t.klient_name && <><span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" /><span className="text-blue-700">{t.klient_name}</span></>}
                    {t.deadline && <><span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" /><span>{t.deadline}</span></>}
                    {t.hotovo_kdy && <><span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" /><span className="text-emerald-700">hotovo {t.hotovo_kdy}</span></>}
                  </div>
                </button>
                {group.key === "archiv" && (
                  <button
                    onClick={() => onUnarchive && onUnarchive(t.id)}
                    title="Vrátit do Naplánované"
                    className="text-xs text-brand-600 hover:text-brand-800 font-semibold px-2 py-1 rounded hover:bg-brand-50"
                  >
                    Obnovit
                  </button>
                )}
                {canDelete && (
                  <button onClick={() => onDelete(t.id)} title="Smazat" className="text-slate-300 hover:text-rose-600 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

